import { createContext, useContext, useSyncExternalStore } from "react";
import { proxy, subscribe } from "valtio";
import { subscribeKey } from "valtio/utils";
import { IJumpMarkItem, IJumpMarkSectionRegistration, IJumpMarksState, IJumpMarksStore } from "src/components-mui/common/jump-marks/jump-marks.types";
import { TResponsivePx } from "src/types/responsive";

interface IJumpMarksContextValue {
    items: readonly IJumpMarkItem[];
    store: IJumpMarksStore;
    stickyTopPx: TResponsivePx;
    registerSection: (registration: IJumpMarkSectionRegistration) => () => void;
    navigateToSection: (id: string) => void;
    scrollToSection: (id: string) => void;
}

export const createJumpMarksState = ({ visibleId }: Pick<IJumpMarksState, "visibleId">) =>
    proxy<IJumpMarksState>({ visibleId, jumpTargetId: null, urlAnchorId: null });

export const createJumpMarksStore = (state: IJumpMarksState): IJumpMarksStore => ({
    getState: () => state,
    subscribe: onChange => subscribe(state, onChange),
    subscribeToJumpEnd: onJumpEnd =>
        subscribeKey(state, "jumpTargetId", jumpTargetId => {
            if (jumpTargetId === null) {
                onJumpEnd();
            }
        }),
});

export const JumpMarksContext = createContext<IJumpMarksContextValue>({
    items: [],
    store: createJumpMarksStore(createJumpMarksState({ visibleId: "" })),
    stickyTopPx: { xs: 0 },
    registerSection: () => () => undefined,
    navigateToSection: () => undefined,
    scrollToSection: () => undefined,
});

export const useJumpMarksNavigation = () => {
    const { items, navigateToSection, scrollToSection } = useContext(JumpMarksContext);

    return { items, navigateToSection, scrollToSection };
};

export const useScrollToSection = () => useContext(JumpMarksContext).scrollToSection;

export const useRegisterJumpMarkSection = () => useContext(JumpMarksContext).registerSection;

export const useJumpMarksStore = () => useContext(JumpMarksContext).store;

export const useJumpMarksStickyTopPx = () => useContext(JumpMarksContext).stickyTopPx;

/** The selector must return a primitive. */
export const useJumpMarksSelector = <T>(selector: (state: Readonly<IJumpMarksState>) => T) => {
    const store = useJumpMarksStore();
    const getSelection = () => selector(store.getState());

    return useSyncExternalStore(store.subscribe, getSelection, getSelection);
};
